import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import {
    Box,
    Checkbox,
    FormControlLabel,
    Grid,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import AcxButton from "components/UI/AcxButton";
import AcxChip from "components/UI/AcxChip";
import AcxConfirmationDialog from "components/UI/AcxConfirmationDialog";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxTable, { IAcxTableColumn } from "components/UI/AcxTable/AcxTable";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import React, { useEffect, useMemo, useState } from "react";
import { ServiceError } from "services/BaseService";
import theme from "Theme/AppTheme";
import { useStore } from "utils/useStore";
import OrganizationService from "../../Organization.service";
import {
    Saml2BindingType,
    Saml2BindingTypeHash,
    saml2BindingTypes,
    SAMLDataRequest,
    SAMLProviderData,
} from "../../types/Organization.type";
import AddButton from "../OrganizationDataSources/components/AddButton";

const primaryProviderKey = "primary";
const logTypes = ["", "Info", "Warning", "Error"];
const logItemCount = 10;

const logColumns = [
    {
        headerLabel: "Date",
        dataKey: "createdOn",
        formatter: (arg) => {
            const dateString = new Date(arg)?.toLocaleString();
            return dateString !== "Invalid Date" ? dateString : "";
        },
    },
    {
        headerLabel: "Type",
        dataKey: "type",
        formatter: (arg) => logTypes[arg],
    },
    {
        headerLabel: "Message",
        dataKey: "message",
    },
];

type Props = {
    isLoading: boolean;
    defaultValues: SAMLDataRequest;
    organizationId?: string;
};

type ValidationErrors = Record<string, string>;

type SamlProviderRow = SAMLProviderData & {
    rowId: string;
    index: number;
    displayName: string;
    bindingTypeLabel: string;
    storageTarget: string;
    hasError: boolean;
};

const SAMLSettings: React.FC<React.PropsWithChildren<Props>> = ({
    isLoading,
    defaultValues,
    organizationId,
}) => {
    const [isLogsOpen, setIsLogsOpen] = useState(false);
    const [SAMLPageNumber, setSAMLPageNumber] = useState(1);
    const [logRows, setLogRows] = useState<any[]>(["loading"]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [enableSamlSso, setEnableSamlSso] = useState(
        getInitialEnabled(defaultValues),
    );
    const [savedSamlEnabled, setSavedSamlEnabled] = useState(
        getInitialEnabled(defaultValues),
    );
    const [isDisableConfirmationOpen, setIsDisableConfirmationOpen] =
        useState(false);
    const [closeProviderFormAfterConfirmedSave, setCloseProviderFormAfterConfirmedSave] =
        useState(false);
    const [providers, setProviders] = useState<SAMLProviderData[]>(() =>
        getInitialProviders(defaultValues),
    );
    const [providerFormIndex, setProviderFormIndex] = useState<number | null>(
        null,
    );
    const [providersBeforeForm, setProvidersBeforeForm] = useState<
        SAMLProviderData[] | null
    >(null);
    const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
        {},
    );
    const messageStore = useStore(MessageStore);

    useEffect(() => {
        const initialProviders = getInitialProviders(defaultValues);
        const initialEnabled = getInitialEnabled(defaultValues);
        setEnableSamlSso(initialEnabled);
        setSavedSamlEnabled(initialEnabled);
        setProviders(initialProviders);
        setProviderFormIndex(null);
        setProvidersBeforeForm(null);
        setValidationErrors({});
    }, [defaultValues]);

    useEffect(() => {
        if (!isLogsOpen || !organizationId) return;
        setLogRows(["loading"]);
        const offset = (SAMLPageNumber - 1) * logItemCount;
        OrganizationService.getLogsForOrganization(
            organizationId,
            offset,
            logItemCount,
        )
            .then((data) => setLogRows(data))
            .catch(() => setLogRows([]));
    }, [SAMLPageNumber, isLogsOpen, organizationId]);

    const enabledProviders = useMemo(
        () => providers.filter((provider) => provider.isEnabled),
        [providers],
    );

    const providerRows = useMemo(
        () =>
            providers.map<SamlProviderRow>((provider, index) => ({
                ...provider,
                rowId: `${provider.id ?? provider.providerKey}-${index}`,
                index,
                displayName:
                    provider.name ||
                    provider.loginButtonText ||
                    provider.providerKey,
                bindingTypeLabel: provider.bindingType
                    ? Saml2BindingTypeHash[provider.bindingType]?.label ?? ""
                    : "",
                storageTarget:
                    index === 0
                        ? "Organization row"
                        : "SAML provider table",
                hasError: providerHasError(provider, validationErrors),
            })),
        [providers, validationErrors],
    );

    const activeProviderPosition = providerFormIndex ?? -1;
    const activeProvider =
        activeProviderPosition >= 0
            ? providers[activeProviderPosition]
            : undefined;

    const updateActiveProvider = (updates: Partial<SAMLProviderData>) => {
        if (activeProviderPosition < 0) {
            return;
        }

        setProviders((current) =>
            normalizeProviderOrder(
                current.map((provider, index) =>
                    index === activeProviderPosition
                        ? { ...provider, ...updates }
                        : provider,
                ),
            ),
        );
    };

    const openCreateProvider = () => {
        setValidationErrors({});
        setEnableSamlSso(true);
        setProviders((current) => {
            setProvidersBeforeForm(current);
            const next = normalizeProviderOrder([
                ...current,
                createNextProvider(current),
            ]);
            setProviderFormIndex(next.length - 1);
            return next;
        });
    };

    const openEditProvider = (providerIndex: number) => {
        setProvidersBeforeForm(providers);
        setProviderFormIndex(providerIndex);
    };

    const closeProviderForm = () => {
        if (providersBeforeForm) {
            setProviders(providersBeforeForm);
        }
        setProvidersBeforeForm(null);
        setProviderFormIndex(null);
        setValidationErrors({});
    };

    const closeProviderFormAfterSave = () => {
        setProvidersBeforeForm(null);
        setProviderFormIndex(null);
    };

    const deleteProvider = (providerIndex: number) => {
        setValidationErrors({});
        setProviders((current) => {
            if (current.length === 1) {
                setProviderFormIndex((currentFormIndex) =>
                    currentFormIndex === null ? null : 0,
                );
                return normalizeProviderOrder([
                    createEmptyProvider(1, primaryProviderKey),
                ]);
            }

            const next = current.filter((_, index) => index !== providerIndex);
            setProviderFormIndex((currentFormIndex) => {
                if (currentFormIndex === null) {
                    return null;
                }
                if (currentFormIndex === providerIndex) {
                    return null;
                }
                return currentFormIndex > providerIndex
                    ? currentFormIndex - 1
                    : currentFormIndex;
            });
            return normalizeProviderOrder(next);
        });
    };

    const moveProvider = (providerIndex: number, direction: -1 | 1) => {
        setValidationErrors({});
        setProviders((current) => {
            const nextIndex = providerIndex + direction;
            if (nextIndex < 0 || nextIndex >= current.length) {
                return current;
            }

            const next = [...current];
            [next[providerIndex], next[nextIndex]] = [
                next[nextIndex],
                next[providerIndex],
            ];
            setProviderFormIndex((currentFormIndex) => {
                if (currentFormIndex === providerIndex) {
                    return nextIndex;
                }
                if (currentFormIndex === nextIndex) {
                    return providerIndex;
                }
                return currentFormIndex;
            });
            return normalizeProviderOrder(next);
        });
    };

    const submit = async (): Promise<boolean> => {
        const providersForSave = normalizeProviderOrder(providers);
        setProviders(providersForSave);

        const errors = validateProviders(enableSamlSso, providersForSave);
        setValidationErrors(errors);
        if (Object.keys(errors).length > 0) {
            const invalidProviderIndex = providersForSave.findIndex((provider) =>
                Object.keys(errors).some(
                    (key) =>
                        key.startsWith(`${provider.providerKey}.`) ||
                        key === `${provider.providerKey}.duplicate`,
                ),
            );
            if (invalidProviderIndex >= 0) {
                setProviderFormIndex(invalidProviderIndex);
            }
            messageStore.logMessage(
                Object.values(errors)[0] ?? "Failed to save SAML options.",
                "error",
            );
            return false;
        }

        setIsSubmitting(true);
        try {
            const savedProviders = await OrganizationService.saveSAMLOptions({
                organizationId,
                enableSamlSso,
                providers: enableSamlSso
                    ? providersForSave.map(normalizeProviderForSave)
                    : [],
            });
            setProviders(normalizeProviderOrder(savedProviders));
            setSavedSamlEnabled(enableSamlSso);
            messageStore.logMessage(
                `Successfully saved SAML options.`,
                "success",
            );
            setProvidersBeforeForm(null);
            return true;
        } catch (error) {
            let message = "Failed to save SAML options.";
            if (error instanceof ServiceError) {
                const errorJson = error.errorJson;
                if (
                    errorJson &&
                    typeof errorJson === "object" &&
                    errorJson.errors &&
                    typeof errorJson.errors === "object"
                ) {
                    message =
                        Object.values(errorJson.errors).flat()[0] ??
                        errorJson.title ??
                        message;
                } else if (errorJson && typeof errorJson === "object") {
                    const modelStateMessage = Object.values(errorJson)
                        .flat()
                        .find((value): value is string =>
                            typeof value === "string",
                        );
                    message =
                        errorJson.detail ??
                        errorJson.message ??
                        errorJson.title ??
                        modelStateMessage ??
                        message;
                } else if (
                    typeof errorJson === "string" &&
                    errorJson.length <= 500
                ) {
                    message = errorJson;
                } else if (await error.isValidationProblem()) {
                    const validationProblem =
                        await error.asValidationProblemDetails();
                    message =
                        Object.values(validationProblem.errors).flat()[0] ??
                        validationProblem.title;
                } else if (error.errorMessage) {
                    message = error.errorMessage;
                } else {
                    message = `${message} (${error.response.status})`;
                }
            }
            console.error("Failed to save SAML options.", error);
            messageStore.logMessage(message, "error");
            return false;
        } finally {
            setIsSubmitting(false);
        }
    };

    const saveSettings = async (closeProviderFormOnSuccess = false) => {
        if (savedSamlEnabled && !enableSamlSso) {
            setCloseProviderFormAfterConfirmedSave(closeProviderFormOnSuccess);
            setIsDisableConfirmationOpen(true);
            return;
        }

        if (await submit() && closeProviderFormOnSuccess) {
            closeProviderFormAfterSave();
        }
    };

    const confirmDisableSaml = async () => {
        if (await submit()) {
            if (closeProviderFormAfterConfirmedSave) {
                closeProviderFormAfterSave();
            }
            setCloseProviderFormAfterConfirmedSave(false);
            setIsDisableConfirmationOpen(false);
        }
    };

    const closeDisableConfirmation = () => {
        setCloseProviderFormAfterConfirmedSave(false);
        setIsDisableConfirmationOpen(false);
    };

    const getActiveFieldError = (fieldName: keyof SAMLProviderData) =>
        activeProvider
            ? validationErrors[
                  `${activeProvider.providerKey}.${String(fieldName)}`
              ]
            : undefined;

    const providerColumns: IAcxTableColumn[] = [
        {
            headerLabel: "Order",
            dataKey: "index",
            formatter: (_value, row: SamlProviderRow) => (
                <Typography fontSize="13px" fontWeight={700}>
                    {row.index + 1}
                </Typography>
            ),
        },
        {
            headerLabel: "Provider",
            dataKey: "displayName",
            formatter: (_value, row: SamlProviderRow) => (
                <Stack direction="row" spacing={1} alignItems="center">
                    <Typography fontSize="13px" fontWeight={700} noWrap>
                        {row.displayName}
                    </Typography>
                    {row.index === 0 && (
                        <AcxChip label="Default" size="small" color="blue" />
                    )}
                </Stack>
            ),
        },
        {
            headerLabel: "Provider Key",
            dataKey: "providerKey",
        },
        {
            headerLabel: "Authenticx Domain",
            dataKey: "authenticxDomain",
            formatter: (value) => value ?? "",
        },
        {
            headerLabel: "Binding",
            dataKey: "bindingTypeLabel",
        },
        {
            headerLabel: "Status",
            dataKey: "isEnabled",
            formatter: (_value, row: SamlProviderRow) =>
                row.hasError ? (
                    <AcxChip label="Needs attention" size="small" color="red" />
                ) : (
                    <AcxChip
                        label={row.isEnabled ? "Enabled" : "Disabled"}
                        size="small"
                        color={row.isEnabled ? "green" : "gray"}
                    />
                ),
        },
        {
            headerLabel: "Storage",
            dataKey: "storageTarget",
        },
        {
            headerLabel: "Actions",
            dataKey: "actions",
            formatter: (_value, row: SamlProviderRow) => (
                <Box
                    sx={{
                        display: "flex",
                        gap: 0.5,
                        justifyContent: "flex-end",
                        width: "100%",
                    }}
                >
                    <Tooltip title="Move up">
                        <span>
                            <IconButton
                                size="small"
                                aria-label="Move provider up"
                                onClick={() => moveProvider(row.index, -1)}
                                disabled={row.index === 0 || isLoading}
                            >
                                <ArrowUpwardIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Move down">
                        <span>
                            <IconButton
                                size="small"
                                aria-label="Move provider down"
                                onClick={() => moveProvider(row.index, 1)}
                                disabled={
                                    row.index >= providers.length - 1 ||
                                    isLoading
                                }
                            >
                                <ArrowDownwardIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Tooltip title="Edit">
                        <IconButton
                            size="small"
                            onClick={() => openEditProvider(row.index)}
                            disabled={isLoading}
                        >
                            <EditIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton
                            size="small"
                            onClick={() => deleteProvider(row.index)}
                            disabled={isLoading}
                        >
                            <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Box>
            ),
        },
    ];

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <AcxDialog
                isOpen={isLogsOpen}
                maxWidth="lg"
                onClose={() => setIsLogsOpen(false)}
                title="SAML Logs"
                text=""
                disableBackdropClose
                dialogContentChildren={
                    <AcxTable
                        rows={logRows}
                        columns={logColumns}
                        isLoading={logRows[0] === "loading"}
                    />
                }
            >
                <AcxButton
                    fullWidth
                    onClick={() => {
                        setSAMLPageNumber((prev) => (prev > 1 ? prev - 1 : 1));
                    }}
                    disabled={SAMLPageNumber === 1 || logRows[0] === "loading"}
                >
                    Previous Page
                </AcxButton>
                <Box sx={{ width: "10rem" }}>Page: {SAMLPageNumber}</Box>
                <AcxButton
                    fullWidth
                    onClick={() => {
                        setSAMLPageNumber((prev) => prev + 1);
                    }}
                    disabled={
                        logRows[0] === "loading" ||
                        logRows.length < logItemCount
                    }
                >
                    Next Page
                </AcxButton>
            </AcxDialog>

            <Stack
                direction={{ xs: "column", md: "row" }}
                alignItems={{ xs: "flex-start", md: "center" }}
                justifyContent="space-between"
                spacing={1.5}
            >
                <Typography
                    sx={{
                        fontSize: theme.typography.pxToRem(12),
                        color: theme.palette.text.secondary,
                    }}
                >
                    {enableSamlSso
                        ? `${enabledProviders.length} enabled provider${
                              enabledProviders.length === 1 ? "" : "s"
                          }`
                        : "SAML SSO disabled"}
                </Typography>
                <Stack
                    direction={{ xs: "column", sm: "row" }}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    spacing={1}
                >
                    <FormControlLabel
                        control={
                            <Checkbox
                                onChange={(e) =>
                                    setEnableSamlSso(e.target.checked)
                                }
                                id="enableSamlSso"
                                name="enableSamlSso"
                                checked={enableSamlSso}
                                disabled={isLoading}
                            />
                        }
                        label="Enable SAML SSO"
                    />
                    <AcxButton
                        onClick={() => {
                            setSAMLPageNumber(1);
                            setIsLogsOpen(true);
                        }}
                    >
                        SAML Logs
                    </AcxButton>
                    <AcxButton
                        onClick={() => void saveSettings()}
                        color="primary"
                        loading={isSubmitting}
                        disabled={isSubmitting || isLoading}
                    >
                        Save Settings
                    </AcxButton>
                </Stack>
            </Stack>

            {validationErrors.form && (
                <Typography color="error" fontSize="13px">
                    {validationErrors.form}
                </Typography>
            )}

            <AcxConfirmationDialog
                isOpen={isDisableConfirmationOpen}
                title="Disable SAML SSO?"
                content="Disabling SAML SSO will remove all SAML provider records for this organization. This action cannot be undone."
                confirmButtonText="Disable SAML SSO"
                cancelButtonText="Cancel"
                onClose={closeDisableConfirmation}
                onConfirm={() => void confirmDisableSaml()}
                isLoading={isSubmitting}
                buttonsDisabled={isSubmitting}
            />

            <Box
                sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    width: "100%",
                }}
            >
                <AddButton onClick={openCreateProvider} disabled={isLoading}>
                    SAML Provider
                </AddButton>
            </Box>

            <AcxTable
                columns={providerColumns}
                rows={providerRows}
                keyField="rowId"
                enableHover
                cellDense
                isLoading={isLoading}
                skeletonRows={isLoading ? 3 : 0}
            />

            <AcxDialog
                title={
                    activeProvider?.id
                        ? "Edit SAML Provider"
                        : "Add SAML Provider"
                }
                isOpen={!!activeProvider}
                onClose={closeProviderForm}
                maxWidth={false}
                contentWidth="min(1100px, calc(100vw - 96px))"
                disableBackdropClose
                dialogContentChildren={
                    activeProvider && (
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 2,
                                pt: 1,
                            }}
                        >
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={activeProvider.isEnabled}
                                        onChange={(event) =>
                                            updateActiveProvider({
                                                isEnabled:
                                                    event.target.checked,
                                            })
                                        }
                                        disabled={activeProviderPosition === 0}
                                    />
                                }
                                label="Enabled"
                            />

                            <Grid container spacing={1.5}>
                                <Grid item xs={12} md={6}>
                                    <AcxMainTextField
                                        skeleton={isLoading}
                                        onChange={(e) =>
                                            updateActiveProvider({
                                                name: e.target.value,
                                            })
                                        }
                                        id="samlProviderName"
                                        labelText="Provider name"
                                        value={activeProvider.name}
                                        showAllErrors
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <AcxMainTextField
                                        skeleton={isLoading}
                                        isDisabled
                                        id="samlProviderKey"
                                        labelText="Provider key"
                                        value={activeProvider.providerKey}
                                        error={
                                            !!getActiveFieldError("providerKey")
                                        }
                                        helperText={getActiveFieldError(
                                            "providerKey",
                                        )}
                                        showAllErrors
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <AcxMainTextField
                                        skeleton={isLoading}
                                        onChange={(e) =>
                                            updateActiveProvider({
                                                authenticxDomain:
                                                    e.target.value,
                                            })
                                        }
                                        id="authenticxDomain"
                                        labelText="Authenticx domain"
                                        value={activeProvider.authenticxDomain}
                                        error={
                                            !!getActiveFieldError(
                                                "authenticxDomain",
                                            )
                                        }
                                        helperText={getActiveFieldError(
                                            "authenticxDomain",
                                        )}
                                        showAllErrors
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <AcxSelectSingle
                                        key={`${activeProvider.providerKey}-${activeProvider.bindingType}`}
                                        inputLabel="Binding type"
                                        id="bindingType"
                                        valueField="value"
                                        labelField="label"
                                        options={saml2BindingTypes}
                                        defaultValue={
                                            activeProvider.bindingType
                                                ? Saml2BindingTypeHash[
                                                      activeProvider.bindingType
                                                  ]
                                                : undefined
                                        }
                                        onChange={(val) =>
                                            updateActiveProvider({
                                                bindingType: val.value,
                                            })
                                        }
                                        error={
                                            !!getActiveFieldError("bindingType")
                                        }
                                        helperText={getActiveFieldError(
                                            "bindingType",
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <AcxMainTextField
                                        skeleton={isLoading}
                                        onChange={(e) =>
                                            updateActiveProvider({
                                                entityId: e.target.value,
                                            })
                                        }
                                        id="entityId"
                                        labelText="Entity ID"
                                        value={activeProvider.entityId}
                                        error={
                                            !!getActiveFieldError("entityId")
                                        }
                                        helperText={getActiveFieldError(
                                            "entityId",
                                        )}
                                        showAllErrors
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <AcxMainTextField
                                        skeleton={isLoading}
                                        onChange={(e) =>
                                            updateActiveProvider({
                                                loginButtonText:
                                                    e.target.value,
                                            })
                                        }
                                        id="loginButtonText"
                                        labelText="Login button text"
                                        value={activeProvider.loginButtonText}
                                        error={
                                            !!getActiveFieldError(
                                                "loginButtonText",
                                            )
                                        }
                                        helperText={getActiveFieldError(
                                            "loginButtonText",
                                        )}
                                        showAllErrors
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <AcxMainTextField
                                        skeleton={isLoading}
                                        onChange={(e) =>
                                            updateActiveProvider({
                                                samlResponseEmailPropertyName:
                                                    e.target.value,
                                            })
                                        }
                                        id="samlResponseEmailPropertyName"
                                        labelText="Response email property name"
                                        value={
                                            activeProvider.samlResponseEmailPropertyName
                                        }
                                        error={
                                            !!getActiveFieldError(
                                                "samlResponseEmailPropertyName",
                                            )
                                        }
                                        helperText={getActiveFieldError(
                                            "samlResponseEmailPropertyName",
                                        )}
                                        showAllErrors
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <AcxMainTextField
                                        skeleton={isLoading}
                                        onChange={(e) =>
                                            updateActiveProvider({
                                                samlResponseFirstNamePropertyName:
                                                    e.target.value,
                                            })
                                        }
                                        id="responseFirstNamePropertyName"
                                        labelText="Response first name property name"
                                        value={
                                            activeProvider.samlResponseFirstNamePropertyName
                                        }
                                        showAllErrors
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <AcxMainTextField
                                        skeleton={isLoading}
                                        onChange={(e) =>
                                            updateActiveProvider({
                                                samlResponseLastNamePropertyName:
                                                    e.target.value,
                                            })
                                        }
                                        id="responseLastNamePropertyName"
                                        labelText="Response last name property name"
                                        value={
                                            activeProvider.samlResponseLastNamePropertyName
                                        }
                                        showAllErrors
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <AcxMainTextField
                                        skeleton={isLoading}
                                        onChange={(e) =>
                                            updateActiveProvider({
                                                singleSignOnServiceUrl:
                                                    e.target.value,
                                            })
                                        }
                                        id="singleSignOnServiceUrl"
                                        labelText="Single sign on service URL"
                                        value={
                                            activeProvider.singleSignOnServiceUrl
                                        }
                                        error={
                                            !!getActiveFieldError(
                                                "singleSignOnServiceUrl",
                                            )
                                        }
                                        helperText={getActiveFieldError(
                                            "singleSignOnServiceUrl",
                                        )}
                                        showAllErrors
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <AcxMainTextField
                                        skeleton={isLoading}
                                        onChange={(e) =>
                                            updateActiveProvider({
                                                singleSignOutServiceUrl:
                                                    e.target.value,
                                            })
                                        }
                                        id="singleSignOutServiceUrl"
                                        labelText="Single sign out service URL"
                                        value={
                                            activeProvider.singleSignOutServiceUrl
                                        }
                                        error={
                                            !!getActiveFieldError(
                                                "singleSignOutServiceUrl",
                                            )
                                        }
                                        helperText={getActiveFieldError(
                                            "singleSignOutServiceUrl",
                                        )}
                                        showAllErrors
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <AcxMainTextField
                                        skeleton={isLoading}
                                        onChange={(e) =>
                                            updateActiveProvider({
                                                certificateInPemFormat:
                                                    e.target.value,
                                            })
                                        }
                                        id="certificateInPemFormat"
                                        labelText="Certificate in PEM format"
                                        value={
                                            activeProvider.certificateInPemFormat
                                        }
                                        error={
                                            !!getActiveFieldError(
                                                "certificateInPemFormat",
                                            )
                                        }
                                        helperText={getActiveFieldError(
                                            "certificateInPemFormat",
                                        )}
                                        showAllErrors
                                        multiline
                                        rows={10}
                                    />
                                </Grid>
                            </Grid>

                            {validationErrors[
                                `${activeProvider.providerKey}.duplicate`
                            ] && (
                                <Typography color="error" fontSize="13px">
                                    {
                                        validationErrors[
                                            `${activeProvider.providerKey}.duplicate`
                                        ]
                                    }
                                </Typography>
                            )}
                        </Box>
                    )
                }
            >
                <AcxButton
                    color="primary"
                    onClick={() => void saveSettings(true)}
                    loading={isSubmitting}
                    disabled={isSubmitting || isLoading}
                >
                    Save Settings
                </AcxButton>
            </AcxDialog>
        </Box>
    );
};

function getInitialEnabled(defaultValues: SAMLDataRequest) {
    return (
        defaultValues.enableSamlSso ||
        defaultValues.providers?.some((provider) => provider.isEnabled) ||
        false
    );
}

function getInitialProviders(defaultValues: SAMLDataRequest) {
    if (defaultValues.providers?.length) {
        return normalizeDefaultProviders(defaultValues.providers);
    }

    if (defaultValues.entityId?.trim()) {
        return [
            {
                providerKey: primaryProviderKey,
                name: defaultValues.loginButtonText || "Primary SAML",
                authenticxDomain: defaultValues.authenticxDomain,
                bindingType: defaultValues.bindingType,
                certificateInPemFormat: defaultValues.certificateInPemFormat,
                entityId: defaultValues.entityId,
                loginButtonText: defaultValues.loginButtonText,
                samlResponseEmailPropertyName:
                    defaultValues.samlResponseEmailPropertyName,
                samlResponseFirstNamePropertyName:
                    defaultValues.samlResponseFirstNamePropertyName,
                samlResponseLastNamePropertyName:
                    defaultValues.samlResponseLastNamePropertyName,
                singleSignOnServiceUrl: defaultValues.singleSignOnServiceUrl,
                singleSignOutServiceUrl: defaultValues.singleSignOutServiceUrl,
                isDefault: true,
                isEnabled: true,
            },
        ];
    }

    return [];
}

function normalizeDefaultProviders(providers: SAMLProviderData[]) {
    const normalizedProviders = providers.map((provider, index) => ({
        ...provider,
        providerKey:
            normalizeProviderKey(provider.providerKey) ||
            (index === 0 ? primaryProviderKey : `provider-${index + 1}`),
        isEnabled: provider.isEnabled !== false,
        isDefault: provider.isDefault === true,
    }));

    if (!normalizedProviders.some((provider) => provider.isDefault)) {
        normalizedProviders[0] = {
            ...normalizedProviders[0],
            isDefault: true,
        };
    }

    return normalizeProviderOrder(normalizedProviders);
}

function normalizeProviderOrder(providers: SAMLProviderData[]) {
    return providers.map((provider, index) => ({
        ...provider,
        providerKey:
            index === 0 ? primaryProviderKey : `provider-${index + 1}`,
        isDefault: index === 0,
        isEnabled: index === 0 ? true : provider.isEnabled !== false,
    }));
}

function createEmptyProvider(
    ordinal: number,
    providerKey = `provider-${ordinal}`,
): SAMLProviderData {
    return {
        providerKey,
        name: ordinal === 1 ? "Primary SAML" : `SAML Provider ${ordinal}`,
        bindingType: Saml2BindingType.HttpPost,
        isDefault: providerKey === primaryProviderKey,
        isEnabled: true,
    };
}

function createNextProvider(providers: SAMLProviderData[]) {
    return createEmptyProvider(providers.length + 1);
}

function validateProviders(enabled: boolean, providers: SAMLProviderData[]) {
    const errors: ValidationErrors = {};
    if (!enabled) {
        return errors;
    }

    const enabledProviders = providers.filter((provider) => provider.isEnabled);
    if (enabledProviders.length === 0) {
        errors.form = "At least one enabled SAML provider is required.";
    }

    if (enabledProviders.filter((provider) => provider.isDefault).length !== 1) {
        errors.form =
            "Exactly one enabled SAML provider must be the default provider.";
    }

    if (
        providers.some((provider) => provider.isDefault && !provider.isEnabled)
    ) {
        errors.form = "A disabled SAML provider cannot be the default provider.";
    }

    const providerKeys = new Map<string, string>();
    const entityIds = new Map<string, string>();
    providers.forEach((provider) => {
        const providerKey = provider.providerKey;
        const normalizedProviderKey = normalizeProviderKey(providerKey);
        setRequiredError(
            errors,
            provider,
            "providerKey",
            "Provider key is required.",
        );
        if (
            normalizedProviderKey &&
            !/^[A-Za-z0-9_.-]+$/.test(normalizedProviderKey)
        ) {
            errors[`${providerKey}.providerKey`] =
                "Provider key can only contain letters, numbers, dots, underscores, and hyphens.";
        }
        setRequiredError(
            errors,
            provider,
            "authenticxDomain",
            "Authenticx domain is required.",
        );
        if (
            provider.authenticxDomain &&
            !isValidHostName(provider.authenticxDomain)
        ) {
            errors[`${providerKey}.authenticxDomain`] =
                "Authenticx domain must be a hostname without a scheme, port, path, query, or fragment.";
        }
        setRequiredError(errors, provider, "entityId", "Entity ID is required.");
        setRequiredError(
            errors,
            provider,
            "loginButtonText",
            "Login button text is required.",
        );
        setRequiredError(
            errors,
            provider,
            "samlResponseEmailPropertyName",
            "Response email property name is required.",
        );
        setRequiredError(
            errors,
            provider,
            "singleSignOnServiceUrl",
            "Single sign on service URL is required.",
        );
        if (
            provider.singleSignOnServiceUrl &&
            !isValidHttpsUrl(provider.singleSignOnServiceUrl)
        ) {
            errors[`${providerKey}.singleSignOnServiceUrl`] =
                "Single sign on service URL must be a valid HTTPS URL.";
        }
        if (
            provider.singleSignOutServiceUrl &&
            !isValidHttpsUrl(provider.singleSignOutServiceUrl)
        ) {
            errors[`${providerKey}.singleSignOutServiceUrl`] =
                "Single sign out service URL must be a valid HTTPS URL.";
        }
        setRequiredError(
            errors,
            provider,
            "certificateInPemFormat",
            "Certificate in PEM format is required.",
        );
        if (!provider.bindingType) {
            errors[`${providerKey}.bindingType`] = "Binding type is required.";
        }

        setDuplicateError(
            errors,
            providerKeys,
            normalizedProviderKey,
            providerKey,
            "Provider key must be unique for this organization.",
        );
        setDuplicateError(
            errors,
            entityIds,
            provider.entityId?.trim().toLowerCase(),
            providerKey,
            "Entity ID must be unique.",
        );
    });

    return errors;
}

function providerHasError(
    provider: SAMLProviderData,
    validationErrors: ValidationErrors,
) {
    return Object.keys(validationErrors).some(
        (key) =>
            key.startsWith(`${provider.providerKey}.`) ||
            key === `${provider.providerKey}.duplicate`,
    );
}

function setRequiredError(
    errors: ValidationErrors,
    provider: SAMLProviderData,
    field: keyof SAMLProviderData,
    message: string,
) {
    const value = provider[field];
    if (
        value === undefined ||
        value === null ||
        (typeof value === "string" && !value.trim())
    ) {
        errors[`${provider.providerKey}.${String(field)}`] = message;
    }
}

function setDuplicateError(
    errors: ValidationErrors,
    seenValues: Map<string, string>,
    value: string | undefined,
    providerKey: string,
    message: string,
) {
    if (!value) {
        return;
    }

    const existingProviderKey = seenValues.get(value);
    if (existingProviderKey) {
        errors[`${providerKey}.duplicate`] = message;
        errors[`${existingProviderKey}.duplicate`] = message;
        return;
    }

    seenValues.set(value, providerKey);
}

function normalizeProviderForSave(provider: SAMLProviderData): SAMLProviderData {
    return {
        ...provider,
        providerKey: normalizeProviderKey(provider.providerKey),
        name: normalizeNullable(provider.name),
        authenticxDomain: normalizeNullable(
            provider.authenticxDomain,
        )?.toLowerCase(),
        certificateInPemFormat: normalizeNullable(
            provider.certificateInPemFormat,
        ),
        entityId: normalizeNullable(provider.entityId),
        loginButtonText: normalizeNullable(provider.loginButtonText),
        samlResponseEmailPropertyName: normalizeNullable(
            provider.samlResponseEmailPropertyName,
        ),
        samlResponseFirstNamePropertyName: normalizeNullable(
            provider.samlResponseFirstNamePropertyName,
        ),
        samlResponseLastNamePropertyName: normalizeNullable(
            provider.samlResponseLastNamePropertyName,
        ),
        singleSignOnServiceUrl: normalizeNullable(
            provider.singleSignOnServiceUrl,
        ),
        singleSignOutServiceUrl: normalizeNullable(
            provider.singleSignOutServiceUrl,
        ),
    };
}

function normalizeProviderKey(providerKey?: string) {
    return providerKey?.trim().toLowerCase() ?? "";
}

function normalizeNullable(value?: string) {
    const normalized = value?.trim();
    return normalized ? normalized : undefined;
}

function isValidHttpsUrl(value: string) {
    try {
        const url = new URL(value);
        return url.protocol === "https:" && !url.username && !url.password;
    } catch {
        return false;
    }
}

function isValidHostName(value: string) {
    const hostName = value.trim().toLowerCase();
    if (
        !hostName ||
        hostName.endsWith(".") ||
        hostName.includes(":") ||
        hostName.includes("/") ||
        hostName.includes("?") ||
        hostName.includes("#")
    ) {
        return false;
    }

    try {
        return new URL(`https://${hostName}`).hostname === hostName;
    } catch {
        return false;
    }
}

export default SAMLSettings;
